<!--
  AttendeeDeleteDrawer.vue
  이벤트 참석자 대량 삭제 드로워 컴포넌트

  - 소프트 삭제와 하드 삭제를 탭으로 구분
  - 하드 삭제 시 위험성 경고 및 확인 문자열 입력 요구
-->
<template>
  <a-drawer
    :visible="visible"
    :width="600"
    title="참여자 삭제"
    placement="right"
    @close="handleClose"
    :closable="true"
    :destroy-on-close="true"
  >
    <div class="delete-drawer-content">
      <!-- 참석자 정보 표시 -->
      <div class="attendee-info-card">
        <div class="info-label">삭제할 참여자</div>
        <div class="info-value">{{ attendeeCount }}명</div>
        <div class="info-hint">선택된 참여자가 삭제됩니다.</div>
      </div>

      <!-- 참여자 ID 정보 목록 -->
      <div class="attendee-id-list" v-if="attendees && attendees.length > 0">
        <div class="id-list-header">
          <i class="fa fa-list"></i>
          <span>삭제 대상 ID 정보</span>
        </div>
        <div class="id-list-content">
          <div
            v-for="(attendee, index) in displayAttendees"
            :key="attendee._id || index"
            class="id-item"
          >
            <span class="id-index">{{ index + 1 }}.</span>
            <span class="id-label">참여자ID:</span>
            <span class="id-value">{{ attendee._id || '-' }}</span>
            <span class="id-separator">|</span>
            <span class="id-label">고객ID:</span>
            <span class="id-value">{{ attendee.customerId || '-' }}</span>
          </div>
          <div v-if="attendees.length > 5" class="id-more">
            ... 외 {{ attendees.length - 5 }}명
          </div>
        </div>
      </div>

      <!-- 로그인 연동 여부 조회 -->
      <div class="sns-check-section">
        <div class="sns-check-header">
          <i class="fa fa-link"></i>
          <span>로그인 연동 여부 조회</span>
        </div>
        <div class="sns-check-content">
          <div class="sns-check-input">
            <a-input
              v-model:value="snsCheckCustomerId"
              placeholder="고객ID를 입력하세요"
              style="flex: 1"
              @pressEnter="checkCustomerSns"
            />
            <a-button
              type="primary"
              @click="checkCustomerSns"
              :loading="snsCheckLoading"
            >
              <i class="fa fa-search" style="margin-right: 4px"></i>
              조회
            </a-button>
          </div>
          <!-- 조회 결과 -->
          <div v-if="snsCheckResult !== null" class="sns-check-result">
            <div v-if="snsCheckResult.length === 0" class="sns-result-empty">
              <i class="fa fa-times-circle" style="color: #52c41a"></i>
              <span>연동된 SNS 로그인 정보가 없습니다.</span>
            </div>
            <div v-else class="sns-result-list">
              <div class="sns-result-warning">
                <i class="fa fa-exclamation-triangle" style="color: #faad14"></i>
                <span>{{ snsCheckResult.length }}개의 SNS 로그인 연동이 있습니다. 영구 삭제 시 함께 삭제됩니다.</span>
              </div>
              <div
                v-for="(sns, index) in snsCheckResult"
                :key="sns._id || index"
                class="sns-result-item"
              >
                <a-tag :color="getSnsTypeColor(sns.snsType)">{{ sns.snsType }}</a-tag>
                <span class="sns-id">{{ sns.snsId }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 탭 메뉴 -->
      <a-tabs v-model:activeKey="activeTab" @change="handleTabChange">
        <a-tab-pane key="soft" tab="일반 삭제">
          <div class="delete-section">
            <div class="delete-type-header">
              <i class="fa fa-trash" style="color: #1890ff; margin-right: 8px"></i>
              <span class="delete-type-title">소프트 삭제 (Soft Delete)</span>
            </div>

            <div class="delete-description">
              <p>참여자를 논리적으로 삭제합니다. 데이터는 데이터베이스에 유지되며 필요시 복구가 가능합니다.</p>
            </div>

            <div class="delete-features">
              <div class="feature-item">
                <i class="fa fa-check-circle" style="color: #52c41a"></i>
                <span>데이터베이스에 데이터 유지</span>
              </div>
              <div class="feature-item">
                <i class="fa fa-check-circle" style="color: #52c41a"></i>
                <span>필요시 복구 가능</span>
              </div>
              <div class="feature-item">
                <i class="fa fa-check-circle" style="color: #52c41a"></i>
                <span>EventAttendee 데이터만 삭제 처리</span>
              </div>
              <div class="feature-item">
                <i class="fa fa-info-circle" style="color: #1890ff"></i>
                <span>참여자 목록에서 숨김 처리 (deleteYn=true)</span>
              </div>
            </div>

            <div class="warning-box info">
              <i class="fa fa-info-circle"></i>
              <div class="warning-content">
                <div class="warning-title">안내</div>
                <div class="warning-text">참여자가 목록에서 숨겨지지만 데이터는 보존됩니다.</div>
              </div>
            </div>

            <div class="action-buttons">
              <a-button @click="handleClose">취소</a-button>
              <a-button
                type="primary"
                @click="handleSoftDelete"
                :loading="softDeleting"
                class="delete-button soft-delete-btn"
              >
                <i class="fa fa-trash" style="margin-right: 4px"></i>
                소프트 삭제 ({{ attendeeCount }}명)
              </a-button>
            </div>
          </div>
        </a-tab-pane>

        <a-tab-pane key="hard" tab="영구 삭제">
          <div class="delete-section">
            <div class="delete-type-header danger">
              <i class="fa fa-exclamation-triangle" style="color: #ff4d4f; margin-right: 8px"></i>
              <span class="delete-type-title">하드 삭제 (Hard Delete)</span>
            </div>

            <div class="delete-description">
              <p style="color: #ff4d4f; font-weight: bold">
                이 작업은 되돌릴 수 없습니다! 참여자와 관련된 모든 데이터가 영구적으로 삭제됩니다.
              </p>
            </div>

            <div class="delete-features">
              <div class="feature-item danger">
                <i class="fa fa-times-circle" style="color: #ff4d4f"></i>
                <span>EventAttendee 데이터 영구 삭제</span>
              </div>
              <div class="feature-item danger">
                <i class="fa fa-times-circle" style="color: #ff4d4f"></i>
                <span>Customer 데이터 영구 삭제</span>
              </div>
              <div class="feature-item danger">
                <i class="fa fa-times-circle" style="color: #ff4d4f"></i>
                <span>CustomerSns (SNS 로그인 연동) 영구 삭제</span>
              </div>
              <div class="feature-item danger">
                <i class="fa fa-times-circle" style="color: #ff4d4f"></i>
                <span>EventMetric (트래킹 데이터) 영구 삭제</span>
              </div>
              <div class="feature-item danger">
                <i class="fa fa-times-circle" style="color: #ff4d4f"></i>
                <span>복구 불가능</span>
              </div>
              <div class="feature-item danger">
                <i class="fa fa-users" style="color: #ff4d4f"></i>
                <span>{{ attendeeCount }}명의 참여자와 연관 데이터가 모두 삭제됩니다</span>
              </div>
            </div>

            <div class="warning-box danger">
              <i class="fa fa-exclamation-triangle"></i>
              <div class="warning-content">
                <div class="warning-title">경고</div>
                <div class="warning-text">
                  이 작업은 되돌릴 수 없습니다. 다음 데이터가 데이터베이스에서 영구적으로 삭제됩니다:
                  <ul style="margin-top: 8px; padding-left: 20px;">
                    <li>EventAttendee (이벤트 참석자 정보)</li>
                    <li>Customer (고객 정보)</li>
                    <li>CustomerSns (SNS 로그인 연동 정보)</li>
                    <li>EventMetric (트래킹 데이터)</li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="confirmation-section">
              <div class="confirmation-label">
                영구 삭제를 확인하려면 <strong>"{{ confirmationKeyword }}"</strong>를 정확히 입력하세요:
              </div>
              <a-input
                v-model:value="confirmationText"
                :placeholder="`${confirmationKeyword} 입력`"
                class="confirmation-input"
                @input="handleConfirmationInput"
              />
              <div class="confirmation-status" v-if="confirmationText">
                <span v-if="isConfirmationValid" class="valid">
                  <i class="fa fa-check-circle"></i> 일치합니다
                </span>
                <span v-else class="invalid">
                  <i class="fa fa-times-circle"></i> 일치하지 않습니다
                </span>
              </div>
            </div>

            <div class="action-buttons">
              <a-button @click="handleClose">취소</a-button>
              <a-button
                danger
                type="primary"
                @click="handleHardDelete"
                :loading="hardDeleting"
                :disabled="!isConfirmationValid"
                class="delete-button hard-delete-btn"
              >
                <i class="fa fa-trash" style="margin-right: 4px"></i>
                영구 삭제 ({{ attendeeCount }}명)
              </a-button>
            </div>
          </div>
        </a-tab-pane>
      </a-tabs>
    </div>
  </a-drawer>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { message } from 'ant-design-vue';
import { twoBtnDialog } from '@/utils/utils';

export default {
  name: 'AttendeeDeleteDrawer',
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    attendeeIds: {
      type: Array,
      required: true,
      default: () => []
    },
    attendees: {
      type: Array,
      default: () => []
    },
    apiInstance: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'delete-success'],
  setup(props, { emit }) {
    const activeTab = ref('soft');
    const confirmationText = ref('');
    const confirmationKeyword = '영구삭제';
    const softDeleting = ref(false);
    const hardDeleting = ref(false);

    // SNS 연동 조회 관련 상태
    const snsCheckCustomerId = ref('');
    const snsCheckLoading = ref(false);
    const snsCheckResult = ref(null);

    // API 인스턴스 확인
    console.log('AttendeeDeleteDrawer - apiInstance:', props.apiInstance);
    console.log('AttendeeDeleteDrawer - bulkDeleteAttendees:', props.apiInstance?.bulkDeleteAttendees);

    // 참석자 수 계산
    const attendeeCount = computed(() => {
      return props.attendeeIds?.length || 0;
    });

    // 표시할 참여자 목록 (최대 5명)
    const displayAttendees = computed(() => {
      return props.attendees?.slice(0, 5) || [];
    });

    // 확인 문자열 유효성 검사
    const isConfirmationValid = computed(() => {
      return confirmationText.value === confirmationKeyword;
    });

    // 드로워 닫기
    const handleClose = () => {
      confirmationText.value = '';
      activeTab.value = 'soft';
      // SNS 조회 상태 초기화
      snsCheckCustomerId.value = '';
      snsCheckResult.value = null;
      emit('close');
    };

    // 탭 변경 시 확인 문자열 초기화
    const handleTabChange = () => {
      confirmationText.value = '';
    };

    // SNS 연동 조회
    const checkCustomerSns = async () => {
      const customerId = snsCheckCustomerId.value.trim();
      if (!customerId) {
        message.warning('고객ID를 입력해주세요.');
        return;
      }

      snsCheckLoading.value = true;
      snsCheckResult.value = null;

      try {
        await new Promise((resolve, reject) => {
          props.apiInstance.getCustomerSnsByCustomerId(
            { customerId },
            (response) => {
              const data = response.data?.data || response.data || [];
              snsCheckResult.value = Array.isArray(data) ? data : [];
              resolve(response);
            },
            (error) => {
              console.error('SNS 연동 조회 실패:', error);
              message.error('SNS 연동 정보 조회 중 오류가 발생했습니다.');
              snsCheckResult.value = [];
              reject(error);
            }
          );
        });
      } catch (error) {
        console.error('SNS 연동 조회 오류:', error);
      } finally {
        snsCheckLoading.value = false;
      }
    };

    // SNS 타입별 색상
    const getSnsTypeColor = (snsType) => {
      const colors = {
        kakao: 'gold',
        apple: 'default',
        wechat: 'green',
        KAKAO: 'gold',
        APPLE: 'default',
        WECHAT: 'green'
      };
      return colors[snsType] || 'blue';
    };

    // 확인 문자열 입력 처리
    const handleConfirmationInput = () => {
      // 입력 처리 로직 (필요시 추가)
    };

    // 소프트 삭제 처리
    const handleSoftDelete = async () => {
      const confirmed = await twoBtnDialog(
        '참여자 삭제 확인',
        `${attendeeCount.value}명의 참여자를 삭제하시겠습니까? 이 작업은 되돌릴 수 있습니다.`,
        '삭제'
      );

      if (confirmed) {
        softDeleting.value = true;
        try {
          await new Promise((resolve, reject) => {
            props.apiInstance.bulkDeleteAttendees(
              {
                attendeeIds: props.attendeeIds,
                deleteType: 'soft'
              },
              (response) => {
                const data = response.data?.data || response.data;
                const deletedCount = data.deletedCount || 0;
                message.success(`${deletedCount}명의 참여자가 성공적으로 삭제되었습니다.`);
                emit('delete-success', 'soft', deletedCount);
                handleClose();
                resolve(response);
              },
              (error) => {
                console.error('소프트 삭제 실패:', error);
                message.error('참여자 삭제 중 오류가 발생했습니다.');
                reject(error);
              }
            );
          });
        } catch (error) {
          console.error('소프트 삭제 오류:', error);
        } finally {
          softDeleting.value = false;
        }
      }
    };

    // 하드 삭제 처리
    const handleHardDelete = async () => {
      if (!isConfirmationValid.value) {
        message.warning('확인 문자열을 정확히 입력해주세요.');
        return;
      }

      const confirmMessage = `정말로 ${attendeeCount.value}명의 참여자를 영구 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없으며 다음 데이터가 영구 삭제됩니다:\n- EventAttendee (참석자 정보)\n- Customer (고객 정보)`;

      const confirmed = await twoBtnDialog(
        '영구 삭제 최종 확인',
        confirmMessage,
        '영구 삭제'
      );

      if (confirmed) {
        hardDeleting.value = true;
        try {
          await new Promise((resolve, reject) => {
            props.apiInstance.bulkDeleteAttendees(
              {
                attendeeIds: props.attendeeIds,
                deleteType: 'hard'
              },
              (response) => {
                const data = response.data?.data || response.data;
                const deletedCount = data.deletedCount || 0;
                const customerDeletedCount = data.customerDeletedCount || 0;
                const customerSnsDeletedCount = data.customerSnsDeletedCount || 0;
                const eventMetricDeletedCount = data.eventMetricDeletedCount || 0;

                // 상세 삭제 결과 메시지 구성
                let resultMessage = `${deletedCount}명의 참석자`;
                if (customerDeletedCount > 0) {
                  resultMessage += `, ${customerDeletedCount}명의 고객`;
                }
                if (customerSnsDeletedCount > 0) {
                  resultMessage += `, ${customerSnsDeletedCount}건의 SNS 연동`;
                }
                if (eventMetricDeletedCount > 0) {
                  resultMessage += `, ${eventMetricDeletedCount}건의 트래킹 데이터`;
                }
                resultMessage += '가 영구 삭제되었습니다.';

                message.success(resultMessage);
                emit('delete-success', 'hard', { deletedCount, customerDeletedCount, customerSnsDeletedCount, eventMetricDeletedCount });
                handleClose();
                resolve(response);
              },
              (error) => {
                console.error('하드 삭제 실패:', error);
                if (error.response?.status === 403) {
                  message.error('하드 삭제 권한이 없습니다. CERTILIFE 권한이 필요합니다.');
                } else {
                  message.error('참여자 영구 삭제 중 오류가 발생했습니다.');
                }
                reject(error);
              }
            );
          });
        } catch (error) {
          console.error('하드 삭제 오류:', error);
        } finally {
          hardDeleting.value = false;
        }
      }
    };

    // props 변경 감지하여 초기화
    watch(() => props.visible, (newVal) => {
      if (newVal) {
        confirmationText.value = '';
        activeTab.value = 'soft';
      }
    });

    return {
      activeTab,
      confirmationText,
      confirmationKeyword,
      softDeleting,
      hardDeleting,
      attendeeCount,
      displayAttendees,
      isConfirmationValid,
      // SNS 연동 조회
      snsCheckCustomerId,
      snsCheckLoading,
      snsCheckResult,
      checkCustomerSns,
      getSnsTypeColor,
      // handlers
      handleClose,
      handleTabChange,
      handleConfirmationInput,
      handleSoftDelete,
      handleHardDelete
    };
  }
};
</script>

<style scoped lang="scss">
.delete-drawer-content {
  padding: 0;
}

.attendee-info-card {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;

  .info-label {
    font-size: 12px;
    color: #666;
    margin-bottom: 8px;
  }

  .info-value {
    font-size: 24px;
    font-weight: bold;
    color: #1890ff;
    margin-bottom: 4px;
  }

  .info-hint {
    font-size: 12px;
    color: #999;
  }
}

.attendee-id-list {
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 24px;

  .id-list-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #666;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e8e8e8;

    i {
      color: #1890ff;
    }
  }

  .id-list-content {
    max-height: 180px;
    overflow-y: auto;
  }

  .id-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 0;
    font-size: 12px;
    border-bottom: 1px dashed #f0f0f0;

    &:last-child {
      border-bottom: none;
    }
  }

  .id-index {
    color: #999;
    min-width: 20px;
  }

  .id-label {
    color: #666;
    font-weight: 500;
  }

  .id-value {
    color: #1890ff;
    font-family: monospace;
    font-size: 11px;
  }

  .id-separator {
    color: #d9d9d9;
    margin: 0 4px;
  }

  .id-more {
    font-size: 12px;
    color: #999;
    text-align: center;
    padding-top: 8px;
    font-style: italic;
  }
}

.sns-check-section {
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 24px;

  .sns-check-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #d46b08;
    margin-bottom: 10px;

    i {
      color: #fa8c16;
    }
  }

  .sns-check-content {
    .sns-check-input {
      display: flex;
      gap: 8px;
      margin-bottom: 12px;
    }

    .sns-check-result {
      background: #fff;
      border-radius: 6px;
      padding: 10px;
    }

    .sns-result-empty {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #52c41a;
      font-size: 13px;
    }

    .sns-result-list {
      .sns-result-warning {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #d48806;
        font-size: 13px;
        margin-bottom: 10px;
        padding-bottom: 8px;
        border-bottom: 1px dashed #ffd591;
      }

      .sns-result-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 0;
        font-size: 12px;

        .sns-id {
          color: #666;
          font-family: monospace;
          font-size: 11px;
        }
      }
    }
  }
}

.delete-section {
  padding: 16px 0;
}

.delete-type-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e8e8e8;

  &.danger {
    border-bottom-color: #ffccc7;
  }

  .delete-type-title {
    font-size: 18px;
    font-weight: bold;
    color: #000;
  }
}

.delete-description {
  margin-bottom: 20px;

  p {
    margin: 0;
    line-height: 1.6;
  }
}

.delete-features {
  margin-bottom: 24px;

  .feature-item {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    font-size: 14px;

    i {
      font-size: 16px;
    }

    &.danger {
      color: #ff4d4f;
    }
  }
}

.warning-box {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 24px;

  &.info {
    background: #e6f7ff;
    border: 1px solid #91d5ff;
    color: #1890ff;

    i {
      color: #1890ff;
      font-size: 16px;
      margin-top: 2px;
    }
  }

  &.danger {
    background: #fff2f0;
    border: 1px solid #ffccc7;
    color: #ff4d4f;

    i {
      color: #ff4d4f;
      font-size: 16px;
      margin-top: 2px;
    }
  }

  .warning-content {
    flex: 1;

    .warning-title {
      font-weight: bold;
      margin-bottom: 4px;
    }

    .warning-text {
      font-size: 13px;
      line-height: 1.5;
    }
  }
}

.confirmation-section {
  margin-bottom: 24px;
  padding: 16px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #d9d9d9;

  .confirmation-label {
    font-size: 14px;
    color: #666;
    margin-bottom: 12px;
  }

  .confirmation-input {
    width: 100%;
    margin-bottom: 8px;
  }

  .confirmation-status {
    font-size: 13px;

    .valid {
      color: #52c41a;

      i {
        margin-right: 4px;
      }
    }

    .invalid {
      color: #ff4d4f;

      i {
        margin-right: 4px;
      }
    }
  }
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.delete-button {
  &.soft-delete-btn {
    background-color: #1890ff;
    border-color: #1890ff;

    &:hover:not(:disabled) {
      background-color: #40a9ff;
      border-color: #40a9ff;
    }
  }

  &.hard-delete-btn {
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

:deep(.ant-tabs-tab) {
  font-size: 14px;
  padding: 8px 0;
}

:deep(.ant-tabs-tab-active) {
  font-weight: bold;
}

:deep(.ant-drawer-header) {
  border-bottom: 1px solid #f0f0f0;
}

:deep(.ant-drawer-body) {
  padding: 24px;
}
</style>
